import { useMutation, useQuery } from '@apollo/client';
import {
  HHBox,
  HHButton,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableRow,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UUIDv4 } from 'uuid-v4-validator';
import {
  GetPhoneStatusForUserDocument,
  SetPhoneForUserDocument,
} from '../types';

const PhoneStatusIndicator = ({
  phoneNumber,
  isInSync,
}: {
  phoneNumber?: string;
  isInSync: boolean;
}): JSX.Element =>
  isInSync ? (
    <span style={{ color: 'green', fontSize: '24px' }}>●</span>
  ) : (
    <span style={{ color: 'red', fontSize: '24px' }}>
      ✖{phoneNumber ? ` (${phoneNumber})` : <i> (not set)</i>}
    </span>
  );

interface PhoneStatus {
  userServicePhone?: string;
  auth0Phones?: string[];
  inSyncAuthService?: boolean;
}

export const MemberPhoneFixTool = (): JSX.Element => {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus | null>({});

  const isValidUuid = UUIDv4.validate(uuid);

  const {
    data: phoneStatusData,
    loading: phoneStatusLoading,
    error: phoneStatusError,
  } = useQuery(GetPhoneStatusForUserDocument, {
    variables: { hhUuid: uuid },
    skip: !isValidUuid, // Skip the query if uuid is not available
  });

  const [setPhoneForUser, { loading: setPhoneLoading, error: setPhoneError }] =
    useMutation(SetPhoneForUserDocument);

  useEffect(() => {
    if (phoneStatusData && isValidUuid) {
      const userServicePhone =
        phoneStatusData.getPhoneStatusForUser.userService?.[0] ?? undefined;

      const auth0Phones = phoneStatusData.getPhoneStatusForUser.auth0;

      const inSyncAuthService =
        auth0Phones.length === 1 && auth0Phones?.[0] === userServicePhone;

      setPhoneStatus({
        userServicePhone,
        auth0Phones: phoneStatusData.getPhoneStatusForUser.auth0 || [],
        inSyncAuthService,
      });
    }
  }, [phoneStatusData, isValidUuid]);

  const handlePhoneSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!phoneStatus?.userServicePhone) {
      return;
    }
    try {
      await setPhoneForUser({
        variables: { hhUuid: uuid, phone: phoneStatus.userServicePhone },
      });
      const inSyncAuthService = true;
      setPhoneStatus({
        ...phoneStatus,
        auth0Phones: [phoneStatus.userServicePhone],
        inSyncAuthService,
      });

      alert('Phone number is updated successfully.');
    } catch (err) {
      console.error(err);
      alert('Phone number was not updated successfully.');
    }
  };

  if (phoneStatusLoading) return <p>Loading .... </p>;
  if (phoneStatusError) return <p> Error: {phoneStatusError.message} </p>;

  return (
    <HHBox bgcolor="#ffffff" padding="2rem" minHeight="100%">
      <HHTypography hhVariant="h1">
        Phone Data for UUID: {isValidUuid && uuid}
      </HHTypography>

      <HHTable
        style={{
          width: '100%',
          maxWidth: '600px',
          tableLayout: 'fixed',
          fontSize: '48px',
        }}
      >
        <HHTableBody>
          <HHTableRow>
            <HHTableCell>
              <HHTypography hhVariant="body1">User Service Phone:</HHTypography>
            </HHTableCell>
            <HHTableCell data-testid="user-service-phone">
              <HHTypography hhVariant="body1">
                {phoneStatus?.userServicePhone}
              </HHTypography>
            </HHTableCell>
          </HHTableRow>

          <HHTableRow>
            <HHTableCell>
              <HHTypography hhVariant="body1">
                Auth0 Phone Sync Status:
              </HHTypography>
            </HHTableCell>
            <HHTableCell data-testid="auth-status">
              <PhoneStatusIndicator
                phoneNumber={phoneStatus?.auth0Phones?.toString()}
                isInSync={!!phoneStatus?.inSyncAuthService}
              />
            </HHTableCell>
          </HHTableRow>
          {/* ACTIONS -------- */}
          <HHTableRow>
            <HHTableCell>
              <HHButton
                onClick={handlePhoneSubmit}
                hhVariant="contained"
                type="submit"
                disabled={
                  !isValidUuid ||
                  (phoneStatus &&
                    phoneStatus.inSyncAuthService &&
                    isValidUuid) ||
                  setPhoneLoading
                }
                style={{ marginLeft: '10px' }}
              >
                {setPhoneLoading ? 'Updating...' : 'Update Phone'}
              </HHButton>
              {setPhoneError && <p>Error: {setPhoneError.message}</p>}
            </HHTableCell>
          </HHTableRow>
        </HHTableBody>
      </HHTable>
    </HHBox>
  );
};
